import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';

import { CompanyHttpService } from '@core/http/company.http.service';

import { SnackbarService } from '@shared/snack-bar/snack-bar.service';
import { parseError } from '@state/errors.parser';
import * as routerActions from '@state/router/router.actions';
import * as companyActions from './company.actions';
import { RouterFacade } from '@state/router';

@Injectable()
export class CompanyEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly companyHttp: CompanyHttpService,
    private readonly snackService: SnackbarService,
    private readonly routerFacade: RouterFacade
  ) {}

  getCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.getCompanies),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.companyHttp.getCompanies(rest).pipe(
          map(response => companyActions.getCompaniesSuccess({ companies: response })),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.getCompanies.type);
            return of(companyActions.getCompaniesError({ errorMessage }));
          })
        );
      })
    )
  );

  getCompaniesBasicDataList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.getCompaniesBasicDataList),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.companyHttp.getCompaniesBasicDataList(rest).pipe(
          map(response => companyActions.getCompaniesBasicDataListSuccess({ companies: response })),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.getCompaniesBasicDataList.type);
            return of(companyActions.getCompaniesBasicDataListError({ errorMessage }));
          })
        );
      })
    )
  );

  getCompanyBasicData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.getCompanyBasicData),
      switchMap(({ payload }) => {
        return this.companyHttp.getCompanyBasicData(payload).pipe(
          map(company => companyActions.getCompanyBasicDataSuccess({ company })),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.getCompanyBasicData.type);
            return of(companyActions.getCompanyBasicDataError({ errorMessage }));
          })
        );
      })
    )
  );

  getCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.getCompany),
      switchMap(({ companyId }) => {
        return this.companyHttp.getCompany(companyId).pipe(
          map(company => companyActions.getCompanySuccess({ company })),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.getCompany.type);
            return of(companyActions.getCompaniesError({ errorMessage }));
          })
        );
      })
    )
  );

  createCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.createCompany),
      switchMap(({ company }) => {
        return this.companyHttp.createCompany(company).pipe(
          map(createdCompany => {
            this.snackService.showInfo('MANAGEMENT.COMPANIES.COMPANY_CREATED');
            return companyActions.createCompanySuccess({ createdCompany });
          }),
          catchError(error => {
            this.snackService.showError('MANAGEMENT.COMPANIES.COMPANY_CREATION_ERROR');
            const errorMessage = parseError(error, companyActions.createCompany.type);
            return of(companyActions.createCompanyError({ errorMessage }));
          })
        );
      })
    )
  );

  createCompanySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.createCompanySuccess),
      map(() => routerActions.changeRoute({ linkParams: ['/management/companies-list'] }))
    )
  );

  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.updateCompany),
      switchMap(({ company, companyId }) => {
        return this.companyHttp.updateCompany(companyId, company).pipe(
          map(updatedCompany => {
            return companyActions.updateCompanySuccess({ updatedCompany });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.updateCompany.type);
            return of(companyActions.updateCompanyError({ errorMessage }));
          })
        );
      })
    )
  );

  updateCompanyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(companyActions.updateCompanyError),
        map(() => {
          this.snackService.showError('COMMON.ERROR.COMPANY-UPDATE-ERROR');
        })
      ),
    { dispatch: false }
  );

  updateCompanySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(companyActions.updateCompanySuccess),
        map(() => {
          this.snackService.showInfo('MANAGEMENT.COMPANIES.COMPANY_UPDATE_SUCCESS');
        })
      ),
    { dispatch: false }
  );

  setIsActiveCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.setIsActiveCompany),
      switchMap(({ companyId, isActive }) => {
        return this.companyHttp.setIsActiveCompany(companyId, isActive).pipe(
          map(updatedCompany => companyActions.setIsActiveCompanySuccess({ updatedCompany })),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.setIsActiveCompany.type);
            return of(companyActions.setIsActiveCompanyError({ errorMessage }));
          })
        );
      })
    )
  );
  setIsActiveCompanyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(companyActions.setIsActiveCompanyError),
        map(() => {
          this.snackService.showError('COMMON.ERROR.COMPANY-UPDATE-ERROR');
        })
      ),
    { dispatch: false }
  );

  setIsActiveCompanySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(companyActions.setIsActiveCompanySuccess),
        map(() => {
          this.snackService.showInfo('MANAGEMENT.COMPANIES.COMPANY_UPDATE_SUCCESS');
        })
      ),
    { dispatch: false }
  );

  // removeCompany$ = createEffect(() => this.actions$.pipe(
  //   ofType(companyActions.removeCompany),
  //   switchMap(({ companyId }) => {
  //     return this.companyHttp.removeCompany(companyId).pipe(
  //       map(() => companyActions.removeCompanySuccess({ removedCompanyId: companyId })),
  //       catchError(error => {
  //         const errorMessage = parseError(error, companyActions.removeCompany.type);
  //         return of(companyActions.updateCompanyError({ errorMessage }));
  //       }),
  //     );
  //   }),
  // ));

  createManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.createManager),
      switchMap(({ payload }) => {
        return this.companyHttp.createManager(payload).pipe(
          map(({ manager, emailSent }) => {
            if (emailSent) {
              this.snackService.showInfo('MANAGEMENT.MANAGERS.MANAGER_CREATED_ACTIVATION_LINK_SENT');
            } else {
              this.snackService.showWarning('MANAGEMENT.MANAGERS.MANAGER_CREATED_ERROR_SENDING_LINK');
            }
            this.routerFacade.changeRoute({
              linkParams: ['management', 'company-details', manager.companyId, 'manager', manager.id],
            });
            return companyActions.createManagerSuccess({ manager });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.createManager.type);
            this.snackService.showError(errorMessage);
            return of(companyActions.createManagerError({ errorMessage }));
          })
        );
      })
    )
  );

  getManagerDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.getManagerDetails),
      switchMap(({ managerId }) => {
        return this.companyHttp.getManagerDetails(managerId).pipe(
          map(manager => {
            return companyActions.getManagerDetailsSuccess({ manager });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.getManagerDetails.type);
            this.snackService.showError(errorMessage);
            return of(companyActions.getManagerDetailsError({ errorMessage }));
          })
        );
      })
    )
  );

  updateManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.updateManager),
      switchMap(({ payload, userId }) => {
        return this.companyHttp.updateManager(payload, userId).pipe(
          map(updatedManager => {
            this.snackService.showInfo('MANAGEMENT.USER_UPDATE_SUCCESS');
            return companyActions.updateManagerSuccess({ manager: updatedManager });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.updateManager.type);
            this.snackService.showError(errorMessage);
            return of(companyActions.updateManagerError({ errorMessage }));
          })
        );
      })
    )
  );

  getManagers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.searchManagersOfCompany),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.companyHttp.searchManagersOfCompany(rest).pipe(
          map(managers => {
            return companyActions.searchManagersOfCompanySuccess({ managers });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.searchManagersOfCompany.type);
            this.snackService.showError(errorMessage);
            return of(companyActions.searchManagersOfCompanyError({ errorMessage }));
          })
        );
      })
    )
  );

  softRemoveManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.softRemoveManager),
      switchMap(({ managerId }) => {
        return this.companyHttp.softRemoveManager(managerId).pipe(
          map(manager => {
            this.snackService.showInfo('MANAGEMENT.COMPANIES.MANAGER_REMOVED');
            return companyActions.softRemoveManagerSuccess({ manager });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.softRemoveManager.type);
            this.snackService.showError(errorMessage);
            return of(companyActions.softRemoveManagerError({ errorMessage }));
          })
        );
      })
    )
  );

  restoreManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.restoreManager),
      switchMap(({ managerId }) => {
        return this.companyHttp.restoreManager(managerId).pipe(
          map(manager => {
            this.snackService.showInfo('MANAGEMENT.MANAGERS.MANAGER_RESTORED');
            return companyActions.restoreManagerSuccess({ manager });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.restoreManager.type);
            this.snackService.showError(errorMessage);
            return of(companyActions.restoreManagerError({ errorMessage }));
          })
        );
      })
    )
  );

  resendMangerActivation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.resendManagerActivation),
      switchMap(({ email, userId }) => {
        return this.companyHttp.resendManagerActivation(email, userId).pipe(
          map(({ emailSent }) => {
            this.snackService.showInfo('COMMON.NOTIFICATION.ACTIVATION_LINK_SENT');
            return companyActions.resendManagerActivationSuccess({ emailSent });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.resendManagerActivation.type);
            this.snackService.showInfo('COMMON.VALIDATOR.UNKNOWN_ERROR');
            return of(companyActions.resendManagerActivationError({ errorMessage }));
          })
        );
      })
    )
  );

  addManagedCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.addManagedCompany),
      switchMap(({ managerId, companyId }) => {
        return this.companyHttp.addManagedCompany({ managerId, companyId }).pipe(
          delay(300),
          map(managedCompany => {
            return companyActions.addManagedCompanySuccess({ managedCompany });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.addManagedCompany.type);
            this.snackService.showError(errorMessage);
            return of(companyActions.addManagedCompanyError({ errorMessage }));
          })
        );
      })
    )
  );

  removeManagedCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.removeManagedCompany),
      switchMap(({ managerId, mocId }) => {
        return this.companyHttp.removeManagedCompany({ managerId, mocId }).pipe(
          delay(300),
          map(removedMoc => {
            return companyActions.removeManagedCompanySuccess({ removedMoc });
          }),
          catchError(error => {
            const errorMessage = parseError(error, companyActions.removeManagedCompany.type);
            this.snackService.showError(errorMessage);
            return of(companyActions.removeManagedCompanyError({ errorMessage }));
          })
        );
      })
    )
  );

  getCompanyOfSelfRegistered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.getCompanyOfSelfRegistered),
      switchMap(() => {
        return this.companyHttp.getCompanyOfSelfRegistered().pipe(
          map(company => companyActions.getCompanyOfSelfRegisteredSuccess({ company })),
          catchError(() => {
            this.snackService.showError('UNABLE_TO_FETCH_COMPANY_OF_SELF_REGISTERED_DATA');
            return of(
              companyActions.getCompanyOfSelfRegisteredError({ errorMessage: 'ERROR_FETCHING_SELF_REG_STATUS' })
            );
          })
        );
      })
    )
  );

  getAllActiveCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyActions.getAllActiveCompanies),
      switchMap(() => {
        return this.companyHttp.getAllActiveCompanies().pipe(
          map(allActiveCompanies => companyActions.getAllActiveCompaniesSuccess({ allActiveCompanies })),
          catchError(() => {
            this.snackService.showError('UNABLE_TO_FETCH_ALL_ACTIVE_COMPANIES');
            return of(
              companyActions.getAllActiveCompaniesError({ errorMessage: 'ERROR_FETCHING_ALL_ACTIVE_COMPANIES' })
            );
          })
        );
      })
    )
  );
}
